import { useState, useEffect } from 'react'
import { nanoid } from '@reduxjs/toolkit'
import { useTranslation } from 'react-i18next'

// MUI Components
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import OutlinedInput from '@mui/material/OutlinedInput'
import InputLabel from '@mui/material/InputLabel'
import FormControl from '@mui/material/FormControl'
import MenuItem from '@mui/material/MenuItem'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Select from '@mui/material/Select'
import Snackbar from '@mui/material/Snackbar'
import LinearProgress from '@mui/material/LinearProgress'
import Alert from '@mui/material/Alert'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import TextField from '@mui/material/TextField'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'

// Icons
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler'
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus'
import WbSunnyIcon from '@mui/icons-material/WbSunny'
import NightsStayIcon from '@mui/icons-material/NightsStay'
import WeekendIcon from '@mui/icons-material/Weekend'

import 'dayjs/locale/ru'
import { t as translate } from 'i18next'

import {
  useAddNewTariffMutation,
  useGetTariffQuery,
  useUpdateTariffMutation
} from './tariff-api'
import { uuidv4 } from '@/utils/helpers'

// ═══════════════════════════════════════════════════════════════════════════
// КОНСТАНТЫ И ТИПЫ ТАРИФОВ
// ═══════════════════════════════════════════════════════════════════════════

// Маппинг UI-типов → бэкенд (tariff_type + calc_mode)
const TARIFF_TYPE_MAP = {
  simple: { tariff_type: 'universal', calc_mode: 'hourly' },
  threshold: { tariff_type: 'universal', calc_mode: 'threshold' },
  progressive: { tariff_type: 'universal', calc_mode: 'staged' },
  daily_cap: { tariff_type: 'universal', calc_mode: 'daily' },
  daily_free: { tariff_type: 'universal', calc_mode: 'daily_free' },
  vehicle_type: { tariff_type: 'universal', calc_mode: 'hourly' },
  time_based: { tariff_type: 'universal', calc_mode: 'hourly' },
  mrp: { tariff_type: 'universal', calc_mode: 'hourly' },
  complex: { tariff_type: 'universal', calc_mode: 'staged' }
}

// Обратный маппинг: calc_mode → UI-тип (для загрузки при редактировании)
const CALC_MODE_TO_UI = {
  hourly: 'simple',
  threshold: 'threshold',
  staged: 'progressive',
  daily: 'daily_cap',
  daily_free: 'daily_free'
}

const TARIFF_TYPES = [
  {
    value: 'simple',
    labelKey: 'tariff_type_simple',
    descriptionKey: 'tariff_type_simple_desc',
    color: 'success'
  },
  {
    value: 'threshold',
    labelKey: 'tariff_type_threshold',
    descriptionKey: 'tariff_type_threshold_desc',
    color: 'warning'
  },
  {
    value: 'progressive',
    labelKey: 'tariff_type_progressive',
    descriptionKey: 'tariff_type_progressive_desc',
    color: 'warning'
  },
  {
    value: 'daily_cap',
    labelKey: 'tariff_type_daily_cap',
    descriptionKey: 'tariff_type_daily_cap_desc',
    color: 'info'
  },
  {
    value: 'daily_free',
    labelKey: 'tariff_type_daily_free',
    descriptionKey: 'tariff_type_daily_free_desc',
    color: 'success'
  },
  {
    value: 'vehicle_type',
    labelKey: 'tariff_type_vehicle',
    descriptionKey: 'tariff_type_vehicle_desc',
    color: 'error'
  },
  {
    value: 'time_based',
    labelKey: 'tariff_type_time_based',
    descriptionKey: 'tariff_type_time_based_desc',
    color: 'secondary'
  },
  {
    value: 'mrp',
    labelKey: 'tariff_type_mrp',
    descriptionKey: 'tariff_type_mrp_desc',
    color: 'default'
  },
  {
    value: 'complex',
    labelKey: 'tariff_type_complex',
    descriptionKey: 'tariff_type_complex_desc',
    color: 'default'
  }
]

const VEHICLE_TYPES = [
  { value: 'car', labelKey: 'light_vehicle', icon: DirectionsCarIcon },
  { value: 'truck', labelKey: 'heavy_vehicle', icon: LocalShippingIcon },
  { value: 'motorcycle', labelKey: 'motorcycle', icon: TwoWheelerIcon },
  {
    value: 'minibus_16',
    labelKey: 'tariff_vehicle_minibus',
    icon: DirectionsBusIcon
  },
  { value: 'bus_32', labelKey: 'tariff_vehicle_bus', icon: DirectionsBusIcon },
  {
    value: 'bus_32_plus',
    labelKey: 'tariff_vehicle_bus_large',
    icon: DirectionsBusIcon
  }
]

// ═══════════════════════════════════════════════════════════════════════════
// ВСПОМОГАТЕЛЬНЫЕ КОМПОНЕНТЫ
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Компонент для отображения подсказки
 */
const HelpText = ({ children }) => (
  <Typography
    variant="caption"
    sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}
  >
    {children}
  </Typography>
)

/**
 * Конвертация минут в читаемый формат
 */
const minutesToReadable = (minutes) => {
  if (!minutes || minutes === 0) return translate('duration_zero_minutes')
  if (minutes < 60) {
    return translate('duration_minutes_short', { value: minutes })
  }
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  if (mins === 0) return translate('duration_hours_short', { value: hours })
  return translate('duration_hours_minutes_short', { hours, minutes: mins })
}

const formatAmount = (amount) => {
  return translate('format_tenge', {
    value: Number(amount || 0).toLocaleString()
  })
}

const formatRatePerHour = (amount) => {
  return `${formatAmount(amount)}/${translate('hour_short')}`
}

/**
 * Секция с заголовком
 */
// Section.propTypes = {
//   title: PropTypes.string.isRequired,
//   icon: PropTypes.node,
//   children: PropTypes.node,
//   color: PropTypes.string
// }
const Section = ({ title, icon, children, color = 'primary.main' }) => (
  <Box sx={{ mb: 3 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      {icon && <Box sx={{ mr: 1, color }}>{icon}</Box>}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, color }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ pl: icon ? 4 : 0 }}>{children}</Box>
  </Box>
)


// ═══════════════════════════════════════════════════════════════════════════
// КОМПОНЕНТ: Прогрессивная шкала
// ═══════════════════════════════════════════════════════════════════════════
// ProgressiveBrackets.propTypes = {
//   brackets: PropTypes.array.isRequired,
//   onChange: PropTypes.func.isRequired
// }
const ProgressiveBrackets = ({ brackets, onChange }) => {
  const addBracket = () => {
    const lastBracket = brackets[brackets.length - 1]
    const newFrom = lastBracket ? lastBracket.to : 0
    onChange([
      ...brackets,
      { from: newFrom, to: newFrom + 60, rate: 0, isFixed: true }
    ])
  }

  const updateBracket = (index, field, value) => {
    const updated = [...brackets]
    updated[index] = { ...updated[index], [field]: value }
    onChange(updated)
  }

  const removeBracket = (index) => {
    onChange(brackets.filter((_, i) => i !== index))
  }

  return (
    <Box>
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell>{translate('tariff_from_minutes')}</TableCell>
              <TableCell>{translate('tariff_to_minutes')}</TableCell>
              <TableCell>{translate('tariff_amount_tenge')}</TableCell>
              <TableCell>{translate('type')}</TableCell>
              <TableCell width={50}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {brackets.map((bracket, index) => (
              <TableRow key={index}>
                <TableCell>
                  <TextField
                    size="small"
                    type="number"
                    value={bracket.from}
                    onChange={(e) =>
                      updateBracket(index, 'from', +e.target.value)
                    }
                    sx={{ width: 80 }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    type="number"
                    value={bracket.to === -1 ? '∞' : bracket.to}
                    onChange={(e) =>
                      updateBracket(
                        index,
                        'to',
                        e.target.value === '∞' ? -1 : +e.target.value
                      )
                    }
                    sx={{ width: 80 }}
                    placeholder="∞"
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    type="number"
                    value={bracket.rate}
                    onChange={(e) =>
                      updateBracket(index, 'rate', +e.target.value)
                    }
                    sx={{ width: 100 }}
                  />
                </TableCell>
                <TableCell>
                  <Select
                    size="small"
                    value={bracket.isFixed ? 'fixed' : 'hourly'}
                    onChange={(e) =>
                      updateBracket(
                        index,
                        'isFixed',
                        e.target.value === 'fixed'
                      )
                    }
                    sx={{ width: 120 }}
                  >
                    <MenuItem value="fixed">
                      {translate('tariff_fixed')}
                    </MenuItem>
                    <MenuItem value="hourly">
                      {translate('hour_short')}
                    </MenuItem>
                  </Select>
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => removeBracket(index)}
                    disabled={brackets.length <= 1}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Button
        startIcon={<AddIcon />}
        onClick={addBracket}
        sx={{ mt: 1 }}
        size="small"
      >
        {translate('tariff_add_range')}
      </Button>

      {/* Предпросмотр */}
      {brackets.length > 0 && (
        <Box sx={{ mt: 2, p: 1.5, bgcolor: 'info.light', borderRadius: 1 }}>
          <Typography variant="caption" sx={{ color: 'info.contrastText' }}>
            <strong>{translate('tariff_calculation_example')}:</strong>
            <br />
            {brackets.map((b, i) => (
              <span key={i}>
                {minutesToReadable(b.from)} -{' '}
                {b.to === -1 ? '∞' : minutesToReadable(b.to)}:{' '}
                {formatAmount(b.rate)}{' '}
                {b.isFixed
                  ? `(${translate('tariff_fixed').toLowerCase()})`
                  : `/${translate('hour_short')}`}
                {i < brackets.length - 1 && <br />}
              </span>
            ))}
          </Typography>
        </Box>
      )}
    </Box>
  )
}



// ═══════════════════════════════════════════════════════════════════════════
// КОМПОНЕНТ: Настройки по типу ТС
// ═══════════════════════════════════════════════════════════════════════════
// VehicleTypeSettings.propTypes = {
//   vehicleTypes: PropTypes.object.isRequired,
//   onChange: PropTypes.func.isRequired
// }
const VehicleTypeSettings = ({ vehicleTypes, onChange }) => {
  const toggleVehicle = (type) => {
    if (vehicleTypes[type]) {
      const updated = { ...vehicleTypes }
      delete updated[type]
      onChange(updated)
    } else {
      onChange({
        ...vehicleTypes,
        [type]: { freeMinutes: 0, rate: 0 }
      })
    }
  }

  const updateVehicle = (type, field, value) => {
    onChange({
      ...vehicleTypes,
      [type]: { ...vehicleTypes[type], [field]: value }
    })
  }

  return (
    <Box>
      {VEHICLE_TYPES.map(({ value, labelKey, icon: Icon }) => (
        <Box key={value} sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!vehicleTypes[value]}
                onChange={() => toggleVehicle(value)}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Icon sx={{ mr: 1, fontSize: 20 }} />
                {translate(labelKey)}
              </Box>
            }
          />

          {vehicleTypes[value] && (
            <Box sx={{ pl: 4, pt: 1, display: 'flex', gap: 2 }}>
              <TextField
                size="small"
                label={translate('tariff_free_minutes_short')}
                type="number"
                value={vehicleTypes[value].freeMinutes || 0}
                onChange={(e) =>
                  updateVehicle(value, 'freeMinutes', +e.target.value)
                }
                sx={{ width: 140 }}
              />
              <TextField
                size="small"
                label={translate('tariff_rate_per_hour')}
                type="number"
                value={vehicleTypes[value].rate || 0}
                onChange={(e) => updateVehicle(value, 'rate', +e.target.value)}
                sx={{ width: 140 }}
              />
            </Box>
          )}
        </Box>
      ))}
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// КОМПОНЕНТ: Настройки День/Ночь
// ═══════════════════════════════════════════════════════════════════════════
// TimeBasedSettings.propTypes = {
//   timeSettings: PropTypes.object.isRequired,
//   onChange: PropTypes.func.isRequired
// }
const TimeBasedSettings = ({ timeSettings, onChange }) => {
  const updatePeriod = (period, field, value) => {
    onChange({
      ...timeSettings,
      [period]: { ...timeSettings[period], [field]: value }
    })
  }

  return (
    <Box>
      {/* Дневной тариф */}
      <Section
        title={translate('tariff_day_tariff')}
        icon={<WbSunnyIcon />}
        color="warning.main"
      >
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <TextField
            size="small"
            label={translate('filter_from')}
            type="time"
            value={timeSettings.day?.startTime || '06:00'}
            onChange={(e) => updatePeriod('day', 'startTime', e.target.value)}
            sx={{ width: 120 }}
            slotProps={{
              inputLabel: {
                shrink: true,
              }
            }}
          />
          <TextField
            size="small"
            label={translate('filter_to')}
            type="time"
            value={timeSettings.day?.endTime || '20:00'}
            onChange={(e) => updatePeriod('day', 'endTime', e.target.value)}
            sx={{ width: 120 }}
            slotProps={{
              inputLabel: {
                shrink: true,
              }
            }}
          />
          <TextField
            size="small"
            label={translate('tariff_free_minutes_short')}
            type="number"
            value={timeSettings.day?.freeMinutes || 0}
            onChange={(e) =>
              updatePeriod('day', 'freeMinutes', +e.target.value)
            }
            sx={{ width: 140 }}
          />
          <TextField
            size="small"
            label={translate('tariff_rate_per_hour')}
            type="number"
            value={timeSettings.day?.rate || 0}
            onChange={(e) => updatePeriod('day', 'rate', +e.target.value)}
            sx={{ width: 140 }}
          />
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={timeSettings.day?.freeLimitEnabled || false}
              onChange={(e) =>
                updatePeriod('day', 'freeLimitEnabled', e.target.checked)
              }
            />
          }
          label={translate('tariff_free_limit_per_day')}
        />
        {timeSettings.day?.freeLimitEnabled && (
          <TextField
            size="small"
            label={translate('tariff_max_times')}
            type="number"
            value={timeSettings.day?.freeLimit || 1}
            onChange={(e) => updatePeriod('day', 'freeLimit', +e.target.value)}
            sx={{ width: 100, ml: 2 }}
          />
        )}
      </Section>

      <Divider sx={{ my: 2 }} />

      {/* Ночной тариф */}
      <Section
        title={translate('tariff_night_tariff')}
        icon={<NightsStayIcon />}
        color="secondary.main"
      >
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <TextField
            size="small"
            label={translate('filter_from')}
            type="time"
            value={timeSettings.night?.startTime || '20:00'}
            onChange={(e) => updatePeriod('night', 'startTime', e.target.value)}
            sx={{ width: 120 }}
            slotProps={{
              inputLabel: {
                shrink: true,
              }
            }}
          />
          <TextField
            size="small"
            label={translate('filter_to')}
            type="time"
            value={timeSettings.night?.endTime || '06:00'}
            onChange={(e) => updatePeriod('night', 'endTime', e.target.value)}
            sx={{ width: 120 }}
            slotProps={{
              inputLabel: {
                shrink: true,
              }
            }}
          />
          <TextField
            size="small"
            label={translate('tariff_free_minutes_short')}
            type="number"
            value={timeSettings.night?.freeMinutes || 0}
            onChange={(e) =>
              updatePeriod('night', 'freeMinutes', +e.target.value)
            }
            sx={{ width: 140 }}
          />
          <TextField
            size="small"
            label={translate('tariff_rate_per_hour')}
            type="number"
            value={timeSettings.night?.rate || 0}
            onChange={(e) => updatePeriod('night', 'rate', +e.target.value)}
            sx={{ width: 140 }}
          />
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={timeSettings.night?.freeLimitEnabled || false}
              onChange={(e) =>
                updatePeriod('night', 'freeLimitEnabled', e.target.checked)
              }
            />
          }
          label={translate('tariff_free_limit_per_day')}
        />
        {timeSettings.night?.freeLimitEnabled && (
          <TextField
            size="small"
            label={translate('tariff_max_times')}
            type="number"
            value={timeSettings.night?.freeLimit || 1}
            onChange={(e) =>
              updatePeriod('night', 'freeLimit', +e.target.value)
            }
            sx={{ width: 100, ml: 2 }}
          />
        )}
      </Section>

      <Divider sx={{ my: 2 }} />

      {/* Выходные */}
      <FormControlLabel
        control={
          <Checkbox
            checked={timeSettings.weekendEnabled || false}
            onChange={(e) =>
              onChange({ ...timeSettings, weekendEnabled: e.target.checked })
            }
          />
        }
        label={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <WeekendIcon sx={{ mr: 1 }} />
            {translate('tariff_separate_weekend_rates')}
          </Box>
        }
      />

      {timeSettings.weekendEnabled && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            {translate('tariff_weekend_days')}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <TextField
              size="small"
              label={translate('tariff_weekend_day_free_minutes')}
              type="number"
              value={timeSettings.weekend?.day?.freeMinutes || 0}
              onChange={(e) =>
                onChange({
                  ...timeSettings,
                  weekend: {
                    ...timeSettings.weekend,
                    day: {
                      ...timeSettings.weekend?.day,
                      freeMinutes: +e.target.value
                    }
                  }
                })
              }
              sx={{ width: 160 }}
            />
            <TextField
              size="small"
              label={translate('tariff_weekend_day_rate')}
              type="number"
              value={timeSettings.weekend?.day?.rate || 0}
              onChange={(e) =>
                onChange({
                  ...timeSettings,
                  weekend: {
                    ...timeSettings.weekend,
                    day: { ...timeSettings.weekend?.day, rate: +e.target.value }
                  }
                })
              }
              sx={{ width: 160 }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              label={translate('tariff_weekend_night_free_minutes')}
              type="number"
              value={timeSettings.weekend?.night?.freeMinutes || 0}
              onChange={(e) =>
                onChange({
                  ...timeSettings,
                  weekend: {
                    ...timeSettings.weekend,
                    night: {
                      ...timeSettings.weekend?.night,
                      freeMinutes: +e.target.value
                    }
                  }
                })
              }
              sx={{ width: 160 }}
            />
            <TextField
              size="small"
              label={translate('tariff_weekend_night_rate')}
              type="number"
              value={timeSettings.weekend?.night?.rate || 0}
              onChange={(e) =>
                onChange({
                  ...timeSettings,
                  weekend: {
                    ...timeSettings.weekend,
                    night: {
                      ...timeSettings.weekend?.night,
                      rate: +e.target.value
                    }
                  }
                })
              }
              sx={{ width: 160 }}
            />
          </Box>
        </Box>
      )}
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// КОМПОНЕНТ: Настройки МРП
// ═══════════════════════════════════════════════════════════════════════════
// MrpSettings.propTypes = {
//   mrpSettings: PropTypes.object.isRequired,
//   onChange: PropTypes.func.isRequired
// }
const MrpSettings = ({ mrpSettings, onChange }) => {
  const currentMrp = 3932 // 2025 год

  const updateRate = (vehicleType, value) => {
    onChange({
      ...mrpSettings,
      rates: { ...mrpSettings.rates, [vehicleType]: value }
    })
  }

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        {translate('tariff_mrp_for_year', {
          year: 2025,
          value: currentMrp.toLocaleString()
        })}
      </Alert>

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell>{translate('vehicle_type')}</TableCell>
              <TableCell>{translate('tariff_mrp_coefficient')}</TableCell>
              <TableCell>{translate('tariff_amount_tenge')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {VEHICLE_TYPES.map(({ value, labelKey, icon: Icon }) => (
              <TableRow key={value}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Icon sx={{ mr: 1, fontSize: 20 }} />
                    {translate(labelKey)}
                  </Box>
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    type="number"
                    slotProps={{
                      htmlInput: {
                        step: 0.1,
                        min: 0
                      }
                    }}
                    value={mrpSettings.rates?.[value] || ''}
                    onChange={(e) =>
                      updateRate(value, parseFloat(e.target.value) || 0)
                    }
                    sx={{ width: 100 }}
                    placeholder="0.0"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {mrpSettings.rates?.[value]
                      ? Math.round(
                          mrpSettings.rates[value] * currentMrp
                        ).toLocaleString()
                      : '—'}{' '}
                    {translate('tenge')}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// ГЛАВНЫЙ КОМПОНЕНТ ФОРМЫ
// ═══════════════════════════════════════════════════════════════════════════
// DialogFormTariff.propTypes = {
//   buildingId: PropTypes.string.isRequired,
//   currentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
//   onClose: PropTypes.func,
//   open: PropTypes.bool,
//   integrationPartnerId: PropTypes.string
// }

const DialogFormTariff = ({
  open = false,
  onClose = () => {},
  currentId = null,
  buildingId,
  integrationPartnerId = ''
}) => {
  const { t } = useTranslation()
  // API Hooks
  const [
    createTariff,
    { isSuccess: addedSuccess, isLoading, isError, error: addedError }
  ] = useAddNewTariffMutation()
  const [
    updateTariff,
    {
      isSuccess: updateSuccess,
      isLoading: updateLoading,
      isError: isUpdateError,
      error: updateError
    }
  ] = useUpdateTariffMutation()
  const { data: tariff } = useGetTariffQuery(buildingId, {
    selectFromResult: ({ data }) => ({
      data: data?.find((item) => item.id === currentId)
    })
  })

  const [openSnackbar, setOpenSnackbar] = useState(false)

  // ═══════════════════════════════════════════════════════════════════════
  // СОСТОЯНИЕ ФОРМЫ
  // ═══════════════════════════════════════════════════════════════════════
  const [form, setForm] = useState({
    // Базовые поля
    id: '',
    code: '',
    name: '',
    isActive: 1,
    building: +buildingId,

    // Тип тарифа
    tariffType: 'simple',

    // Простой тариф
    freeMinutes: 0,
    amount: 0,

    // С порогом
    thresholdMinutes: 0,
    dailyRate: 0,

    // С лимитом за сутки
    maxDaily: null,

    // Прогрессивная шкала
    brackets: [{ from: 0, to: 60, rate: 0, isFixed: true }],

    // По типу ТС
    vehicleTypes: {},

    // День/Ночь
    timeSettings: {
      day: { startTime: '06:00', endTime: '20:00', freeMinutes: 0, rate: 0 },
      night: { startTime: '20:00', endTime: '06:00', freeMinutes: 0, rate: 0 },
      weekendEnabled: false,
      weekend: { day: {}, night: {} }
    },

    // МРП
    mrpSettings: { rates: {} },

    // JSON для сложных правил
    rulesJson: null
  })

  const validateForm =
    form.name &&
    (form.amount ||
      form.brackets?.length > 0 ||
      Object.keys(form.vehicleTypes).length > 0)

  // ═══════════════════════════════════════════════════════════════════════
  // ОБРАБОТЧИКИ
  // ═══════════════════════════════════════════════════════════════════════
  const handleChange = ({ target }) => {
    setForm({
      ...form,
      [target.name]: target.value
    })
  }

  const handleSnackOpen = () => setOpenSnackbar(true)
  const handleSnackClose = (event, reason) => {
    if (reason === 'clickaway') return
    setOpenSnackbar(false)
  }

  const resetForm = () => {
    setForm({
      id: '',
      code: '',
      name: '',
      isActive: 1,
      building: +buildingId,
      tariffType: 'simple',
      freeMinutes: 0,
      amount: 0,
      thresholdMinutes: 0,
      dailyRate: 0,
      maxDaily: null,
      brackets: [{ from: 0, to: 60, rate: 0, isFixed: true }],
      vehicleTypes: {},
      timeSettings: {
        day: { startTime: '06:00', endTime: '20:00', freeMinutes: 0, rate: 0 },
        night: {
          startTime: '20:00',
          endTime: '06:00',
          freeMinutes: 0,
          rate: 0
        },
        weekendEnabled: false,
        weekend: { day: {}, night: {} }
      },
      mrpSettings: { rates: {} },
      rulesJson: null
    })
  }

  const onSuccess = () => {
    resetForm()
    onClose()
    handleSnackOpen()
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  // ═══════════════════════════════════════════════════════════════════════
  // СБОРКА JSON ПРАВИЛ
  // ═══════════════════════════════════════════════════════════════════════
  const buildRulesJson = () => {
    switch (form.tariffType) {
      case 'simple':
        // для UniversalCalculator hourly-режима
        return {
          hourly_rate: form.amount,
          free_minutes: form.freeMinutes
        }

      case 'threshold':
        return {
          free_minutes: form.freeMinutes,
          base_rate: form.amount,
          threshold_minutes: form.thresholdMinutes,
          extra_rate: form.dailyRate
        }

      case 'daily_cap':
        return {
          type: 'daily_cap',
          free_minutes: form.freeMinutes,
          rate: form.amount,
          max_daily: form.maxDaily
        }

      case 'progressive':
        return {
          type: 'progressive',
          brackets: form.brackets.map((b) => ({
            from: b.from,
            to: b.to,
            rate: b.rate,
            is_fixed: b.isFixed
          }))
        }

      case 'vehicle_type':
        return {
          type: 'vehicle_type',
          vehicle_types: form.vehicleTypes
        }

      case 'time_based':
        return {
          type: 'time_based',
          ...form.timeSettings
        }

      case 'mrp':
        return {
          type: 'mrp',
          mrp_rates: form.mrpSettings.rates
        }

      case 'complex':
        return {
          type: 'complex',
          vehicle_types: form.vehicleTypes,
          time_settings: form.timeSettings,
          mrp_settings: form.mrpSettings
        }

      default:
        return { type: form.tariffType }
    }
  }

  // ═══════════════════════════════════════════════════════════════════════
  // ОТПРАВКА ФОРМЫ
  // ═══════════════════════════════════════════════════════════════════════
  const handleSubmit = async () => {
    const rulesJson = buildRulesJson()

    // Получаем правильные tariff_type и calc_mode для бэкенда
    const backendType = TARIFF_TYPE_MAP[form.tariffType] || {
      tariff_type: 'universal',
      calc_mode: 'hourly'
    }

    const payload = {
      code: form.code || nanoid(7),
      name: form.name,
      is_active: Boolean(form.isActive),
      building_id: +buildingId,

      // Базовые поля
      amount: +form.amount,
      free_minutes: +form.freeMinutes,
      max_sum: 0,

      // Тип и режим — маппинг на бэкенд-значения
      tariff_type: backendType.tariff_type,
      calc_mode: backendType.calc_mode,
      threshold_minutes: form.thresholdMinutes ? +form.thresholdMinutes : null,
      daily_rate: form.dailyRate ? +form.dailyRate : null,
      max_daily: form.maxDaily ? +form.maxDaily : null,

      // JSON правила
      rules: rulesJson
    }

    if (integrationPartnerId) {
      payload.integration_partner_id = integrationPartnerId
    }

    try {
      if (currentId) {
        await updateTariff({ id: currentId, ...payload }).unwrap()
      } else {
        await createTariff({ id: uuidv4(), ...payload }).unwrap()
      }
      onSuccess()
    } catch (err) {
      console.log('Error saving tariff:', err)
    }
  }

  // ═══════════════════════════════════════════════════════════════════════
  // ЗАГРУЗКА ДАННЫХ ПРИ РЕДАКТИРОВАНИИ
  // ═══════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (currentId && tariff) {
      // Парсим rules JSON если есть
      let parsedRules = {}
      try {
        if (tariff.rules) {
          parsedRules =
            typeof tariff.rules === 'string'
              ? JSON.parse(tariff.rules)
              : tariff.rules
        }
      } catch (e) {
        console.log('Error parsing rules:', e)
      }

      setForm({
        id: tariff.id,
        code: tariff.code,
        name: tariff.name,
        isActive: tariff.is_active ? 1 : 0,
        building: tariff.building_id,

        // Обратный маппинг calc_mode → UI-тип
        tariffType: CALC_MODE_TO_UI[tariff.calc_mode] || 'simple',
        freeMinutes: tariff.free_minutes || 0,
        amount: tariff.amount || 0,
        thresholdMinutes: tariff.threshold_minutes || 0,
        dailyRate: tariff.daily_rate || 0,
        maxDaily: tariff.max_daily || null,

        brackets: parsedRules.brackets || [
          { from: 0, to: 60, rate: 0, isFixed: true }
        ],
        vehicleTypes: parsedRules.vehicle_types || {},
        timeSettings:
          parsedRules.time_settings || parsedRules.day
            ? parsedRules
            : {
                day: {
                  startTime: '06:00',
                  endTime: '20:00',
                  freeMinutes: 0,
                  rate: 0
                },
                night: {
                  startTime: '20:00',
                  endTime: '06:00',
                  freeMinutes: 0,
                  rate: 0
                },
                weekendEnabled: false,
                weekend: { day: {}, night: {} }
              },
        mrpSettings: { rates: parsedRules.mrp_rates || {} },
        rulesJson: parsedRules
      })
    }
  }, [currentId, tariff])

  // ═══════════════════════════════════════════════════════════════════════
  // РЕНДЕР
  // ═══════════════════════════════════════════════════════════════════════
  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        slotProps={{
          paper: {
            sx: { maxHeight: '90vh' }
          }
        }}
      >
        {(isLoading || updateLoading) && <LinearProgress />}

        <DialogTitle>
          {currentId ? t('editing') : t('add_a_tariff')}
          <IconButton
            onClick={handleClose}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'grey.500' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3 }}>
          {/* ═══════════════ БАЗОВЫЕ ПОЛЯ ═══════════════ */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <FormControl sx={{ flex: 2 }}>
              <InputLabel>{t('payment_partners_name')}</InputLabel>
              <OutlinedInput
                name="name"
                value={form.name}
                onChange={handleChange}
                label={t('payment_partners_name')}
              />
            </FormControl>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel>{t('tariff_code_for_payment_systems')}</InputLabel>
              <OutlinedInput
                name="code"
                value={form.code}
                onChange={handleChange}
                label={t('tariff_code_for_payment_systems')}
                placeholder={t('tariff_placeholder_auto')}
              />
            </FormControl>
          </Box>

          {/* ═══════════════ ТИП ТАРИФА ═══════════════ */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>{t('tariff_type_label')}</InputLabel>
            <Select
              name="tariffType"
              value={form.tariffType}
              onChange={handleChange}
              label={t('tariff_type_label')}
            >
              {TARIFF_TYPES.map(({ value, labelKey, descriptionKey, icon }) => (
                <MenuItem key={value} value={value}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: 8 }}>{icon}</span>
                    <Box>
                      <Typography variant="body1">{t(labelKey)}</Typography>
                      <Typography variant="caption" sx={{
                        color: "text.secondary"
                      }}>
                        {t(descriptionKey)}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider sx={{ my: 3 }} />

          {/* ═══════════════ ПРОСТОЙ ТАРИФ ═══════════════ */}
          {form.tariffType === 'simple' && (
            <Section
              title={t('tariff_simple_section')}
              icon={<AccessTimeIcon />}
            >
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label={t('tariff_free_minutes_label')}
                  type="number"
                  value={form.freeMinutes}
                  onChange={(e) =>
                    setForm({ ...form, freeMinutes: +e.target.value })
                  }
                  sx={{ width: 180 }}
                />
                <TextField
                  label={t('tariff_rate_per_hour')}
                  type="number"
                  value={form.amount}
                  onChange={(e) =>
                    setForm({ ...form, amount: +e.target.value })
                  }
                  sx={{ width: 180 }}
                />
              </Box>
              {form.freeMinutes > 0 && (
                <HelpText>
                  {t('tariff_simple_help', {
                    minutes: minutesToReadable(form.freeMinutes),
                    rate: formatRatePerHour(form.amount)
                  })}
                </HelpText>
              )}
            </Section>
          )}

          {/* ═══════════════ С ПОРОГОМ ═══════════════ */}
          {form.tariffType === 'threshold' && (
            <Section
              title={t('tariff_threshold_section')}
              icon={<AccessTimeIcon />}
              color="warning.main"
            >
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <TextField
                  label={t('tariff_free_minutes_label')}
                  type="number"
                  value={form.freeMinutes}
                  onChange={(e) =>
                    setForm({ ...form, freeMinutes: +e.target.value })
                  }
                  sx={{ width: 160 }}
                />
                <TextField
                  label={t('tariff_base_amount')}
                  type="number"
                  value={form.amount}
                  onChange={(e) =>
                    setForm({ ...form, amount: +e.target.value })
                  }
                  sx={{ width: 160 }}
                />
                <TextField
                  label={t('tariff_threshold_minutes')}
                  type="number"
                  value={form.thresholdMinutes}
                  onChange={(e) =>
                    setForm({ ...form, thresholdMinutes: +e.target.value })
                  }
                  sx={{ width: 160 }}
                />
                <TextField
                  label={t('tariff_extra_rate_per_hour')}
                  type="number"
                  value={form.dailyRate}
                  onChange={(e) =>
                    setForm({ ...form, dailyRate: +e.target.value })
                  }
                  sx={{ width: 160 }}
                />
              </Box>

              {form.thresholdMinutes > 0 && (
                <Box sx={{ p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                  <Typography variant="body2">
                    <strong>{t('tariff_logic')}:</strong>
                    <br />•{' '}
                    {t('tariff_threshold_logic_until', {
                      minutes: minutesToReadable(form.thresholdMinutes),
                      amount: formatAmount(form.amount)
                    })}
                    <br />•{' '}
                    {t('tariff_threshold_logic_after', {
                      baseAmount: formatAmount(form.amount),
                      extraRate: formatAmount(form.dailyRate)
                    })}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption">
                    <strong>{t('tariff_example')}:</strong>{' '}
                    {Math.round(form.thresholdMinutes / 60) + 2}{' '}
                    {t('hour_short')} = {formatAmount(form.amount)} + (2 ×{' '}
                    {formatAmount(form.dailyRate)}) ={' '}
                    <strong>
                      {formatAmount(form.amount + 2 * form.dailyRate)}
                    </strong>
                  </Typography>
                </Box>
              )}
            </Section>
          )}

          {/* ═══════════════ С ЛИМИТОМ ЗА СУТКИ ═══════════════ */}
          {form.tariffType === 'daily_cap' && (
            <Section
              title={t('tariff_daily_cap_section')}
              icon={<AccessTimeIcon />}
              color="info.main"
            >
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label={t('tariff_free_minutes_label')}
                  type="number"
                  value={form.freeMinutes}
                  onChange={(e) =>
                    setForm({ ...form, freeMinutes: +e.target.value })
                  }
                  sx={{ width: 160 }}
                />
                <TextField
                  label={t('tariff_rate_per_hour')}
                  type="number"
                  value={form.amount}
                  onChange={(e) =>
                    setForm({ ...form, amount: +e.target.value })
                  }
                  sx={{ width: 160 }}
                />
                <TextField
                  label={t('tariff_max_daily_amount')}
                  type="number"
                  value={form.maxDaily || ''}
                  onChange={(e) =>
                    setForm({ ...form, maxDaily: +e.target.value || null })
                  }
                  sx={{ width: 180 }}
                  placeholder={t('tariff_without_limit')}
                />
              </Box>
              <HelpText>
                {t('tariff_daily_cap_help', {
                  rate: formatRatePerHour(350),
                  limit: formatAmount(1000)
                })}
              </HelpText>
            </Section>
          )}

          {/* ═══════════════ ПРОГРЕССИВНЫЙ ═══════════════ */}
          {form.tariffType === 'progressive' && (
            <Section
              title={t('tariff_progressive_section')}
              icon={<AccessTimeIcon />}
              color="warning.main"
            >
              <ProgressiveBrackets
                brackets={form.brackets}
                onChange={(brackets) => setForm({ ...form, brackets })}
              />
            </Section>
          )}

          {/* ═══════════════ ПО ТИПУ ТС ═══════════════ */}
          {form.tariffType === 'vehicle_type' && (
            <Section
              title={t('tariff_vehicle_section')}
              icon={<DirectionsCarIcon />}
              color="error.main"
            >
              <VehicleTypeSettings
                vehicleTypes={form.vehicleTypes}
                onChange={(vehicleTypes) => setForm({ ...form, vehicleTypes })}
              />
            </Section>
          )}

          {/* ═══════════════ ДЕНЬ/НОЧЬ ═══════════════ */}
          {form.tariffType === 'time_based' && (
            <TimeBasedSettings
              timeSettings={form.timeSettings}
              onChange={(timeSettings) => setForm({ ...form, timeSettings })}
            />
          )}

          {/* ═══════════════ МРП ═══════════════ */}
          {form.tariffType === 'mrp' && (
            <Section
              title={t('tariff_by_mrp_section')}
              icon={<AccessTimeIcon />}
            >
              <MrpSettings
                mrpSettings={form.mrpSettings}
                onChange={(mrpSettings) => setForm({ ...form, mrpSettings })}
              />
            </Section>
          )}

          {/* ═══════════════ КОМПЛЕКСНЫЙ ═══════════════ */}
          {form.tariffType === 'complex' && (
            <Box>
              <Alert severity="info" sx={{ mb: 3 }}>
                {t('tariff_complex_info')}
              </Alert>

              <Section
                title={t('tariff_transport_types')}
                icon={<DirectionsCarIcon />}
              >
                <VehicleTypeSettings
                  vehicleTypes={form.vehicleTypes}
                  onChange={(vehicleTypes) =>
                    setForm({ ...form, vehicleTypes })
                  }
                />
              </Section>

              <Divider sx={{ my: 3 }} />

              <TimeBasedSettings
                timeSettings={form.timeSettings}
                onChange={(timeSettings) => setForm({ ...form, timeSettings })}
              />
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          {/* ═══════════════ СТАТУС ═══════════════ */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('select_status')}</InputLabel>
            <Select
              name="isActive"
              value={form.isActive}
              onChange={handleChange}
              label={t('select_status')}
            >
              <MenuItem value={1}>
                {t('payment_partners_status_active')}
              </MenuItem>
              <MenuItem value={0}>
                {t('payment_partners_status_inactive')}
              </MenuItem>
            </Select>
          </FormControl>

          {/* ═══════════════ КНОПКА СОХРАНЕНИЯ ═══════════════ */}
          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={!validateForm}
            onClick={handleSubmit}
            sx={{ mt: 2 }}
          >
            {currentId ? t('save') : t('add')}
          </Button>
        </DialogContent>
      </Dialog>

      {/* ═══════════════ УВЕДОМЛЕНИЯ ═══════════════ */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackClose}
      >
        {addedSuccess || updateSuccess ? (
          <Alert severity="success" variant="filled" onClose={handleSnackClose}>
            {t('saved', { defaultValue: 'Сохранено' })}!
          </Alert>
        ) : (
          (isError || isUpdateError) && (
            <Alert severity="error" variant="filled">
              {addedError?.data?.message ||
                updateError?.data?.message ||
                t('there_was_an_unknown_error')}
            </Alert>
          )
        )}
      </Snackbar>
    </>
  );
}

export default DialogFormTariff